import React, { useRef, useState, useEffect } from 'react';
import DashboardHeader from '../components/common/DashboardHeader';
import DashboardChartCard from '../components/common/DashboardChartCard';
import DashboardMetricCard from '../components/common/DashboardMetricCard';
import DashboardReportHeader from '../components/common/DashboardReportHeader';
import { Box, Grid, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Alert, FormControl, InputLabel, Select, MenuItem, LinearProgress } from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LoyaltyIcon from '@mui/icons-material/Loyalty';
import { Gauge, gaugeClasses } from '@mui/x-charts/Gauge';
import { detectThemeSync, watchThemeChanges } from '../utils/themeUtils';
import { getGuestStatistics, getGuestMonthlyGrowth, getTopCustomers, getGuestLocationDistribution, getGuestAgeGroupDistribution, getRepeatedGuestAgeGroupDistribution } from '../services/api';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment';
import {
  getInsightsChartPrimaryGridProps,
  getInsightsChartSecondaryGridProps,
  getInsightsGridSpacing,
  getInsightsMetricGridProps,
  getInsightsSplitChartGridProps,
} from '../utils/insightsDashboardLayout';

const ROOMNEXA_LOGO_URL = '/static/topbar/logo_ed_dark.png';

const loadRoomnexaLogoDataUrl = async () => {
  try {
    const response = await fetch(ROOMNEXA_LOGO_URL);
    if (!response.ok) return null;
    const logoBlob = await response.blob();
    const objectUrl = URL.createObjectURL(logoBlob);
    try {
      const image = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = objectUrl;
      });
      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth || 300;
      canvas.height = image.naturalHeight || 87;
      const context = canvas.getContext('2d');
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/png');
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  } catch (error) {
    return null;
  }
};

const CustomerHubPage = () => {
  // Initialize theme synchronously to prevent flash
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());

  // Watch for theme changes
  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });
    return cleanup;
  }, []);

  // State for data
  const [statistics, setStatistics] = useState(null);
  const [monthlyGrowth, setMonthlyGrowth] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [locationDistribution, setLocationDistribution] = useState({ country: [], state: [], city: [] });
  const [locationGroupBy, setLocationGroupBy] = useState('state');
  const [ageGroupDistribution, setAgeGroupDistribution] = useState([]);
  const [repeatedGuestAgeDistribution, setRepeatedGuestAgeDistribution] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(() => moment().startOf('month'));
  const [endDate, setEndDate] = useState(() => moment().endOf('month'));
  const [pdfExporting, setPdfExporting] = useState(false);
  const fetchRequestRef = useRef(0);
  const reportRef = useRef(null);
  const exportInProgressRef = useRef(false);

  // Fetch all data
  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  const fetchData = async () => {
    const requestId = fetchRequestRef.current + 1;
    fetchRequestRef.current = requestId;
    setLoading(true);
    setError(null);
    try {
      const startDateParam = startDate?.isValid?.() ? startDate.format('YYYY-MM-DD') : null;
      const endDateParam = endDate?.isValid?.() ? endDate.format('YYYY-MM-DD') : null;
      const applyIfLatest = (callback) => {
        if (fetchRequestRef.current === requestId) callback();
      };

      const requests = [
        getGuestStatistics(startDateParam, endDateParam).then((statsData) => {
          applyIfLatest(() => setStatistics(statsData));
        }),
        getGuestMonthlyGrowth(startDateParam, endDateParam).then((growthData) => {
          applyIfLatest(() => setMonthlyGrowth(growthData.data || []));
        }),
        getTopCustomers(startDateParam, endDateParam).then((topCustomersData) => {
          applyIfLatest(() => setTopCustomers(topCustomersData.data || []));
        }),
        Promise.all([
          getGuestLocationDistribution('country', startDateParam, endDateParam),
          getGuestLocationDistribution('state', startDateParam, endDateParam),
          getGuestLocationDistribution('city', startDateParam, endDateParam),
        ]).then(([locationCountry, locationState, locationCity]) => {
          applyIfLatest(() => setLocationDistribution({
            country: locationCountry.data || [],
            state: locationState.data || [],
            city: locationCity.data || [],
          }));
        }),
        getGuestAgeGroupDistribution(startDateParam, endDateParam).then((ageGroupData) => {
          applyIfLatest(() => setAgeGroupDistribution(ageGroupData.data || []));
        }),
        getRepeatedGuestAgeGroupDistribution(startDateParam, endDateParam).then((repeatedAgeData) => {
          applyIfLatest(() => setRepeatedGuestAgeDistribution(repeatedAgeData));
        }),
      ];

      const results = await Promise.allSettled(requests);
      if (results.every((result) => result.status === 'rejected')) {
        applyIfLatest(() => setError('Failed to load dashboard data. Please try again.'));
      }
    } catch (err) {
      if (fetchRequestRef.current === requestId) {
        setError('Failed to load dashboard data. Please try again.');
      }
    } finally {
      if (fetchRequestRef.current === requestId) {
        setLoading(false);
      }
    }
  };

  // Theme-aware colors
  const getThemeColors = () => {
    if (currentTheme === 'dark') {
      return {
        total: '#ff6b7d',
        active: '#f8e082',
        new: '#66d4a8',
        loyalty: '#8ac9ff',
      };
    } else {
      return {
        total: '#ff4861',
        active: '#f6da6e',
        new: '#4ce1b6',
        loyalty: '#70bbfd',
      };
    }
  };

  const themeColors = getThemeColors();
  const isDark = currentTheme === 'dark';

  // Format currency
  const formatCurrency = (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Get total guests as the base for all gauge max values
  const totalGuestsMax = statistics?.total_guests || 100;
  const formatNumber = (value) => Number(value || 0).toLocaleString('en-IN');
  const chartPalette = [
    themeColors.total,
    themeColors.active,
    themeColors.new,
    themeColors.loyalty,
    '#ff9f43',
    '#8e7dff',
    '#26c6da',
    '#66bb6a',
    '#ff4861',
    '#ab47bc',
  ];
  const locationChartData = (locationDistribution[locationGroupBy] || [])
    .map((item, index) => ({
      name: item.name,
      value: Number(item.value || 0),
      color: chartPalette[index % chartPalette.length],
    }))
    .filter((item) => item.value > 0);
  const ageChartData = (ageGroupDistribution || []).map((item, index) => ({
    name: `${item.name} years`,
    value: Number(item.value || 0),
    color: chartPalette[(index + 2) % chartPalette.length],
  }));
  const repeatedAgeChartData = (repeatedGuestAgeDistribution?.data || [])
    .map((item, index) => ({
      name: `${item.name} yrs`,
      value: Number(item.value || 0),
      color: chartPalette[(index + 4) % chartPalette.length],
    }))
    .filter((item) => item.value > 0);
  const repeatedGuestRate = Number(repeatedGuestAgeDistribution?.repeated_percentage || 0);
  const repeatedGuestCount = Number(repeatedGuestAgeDistribution?.repeated_guests || 0);
  const repeatedGuestTotal = Number(repeatedGuestAgeDistribution?.total_guests || 0);
  const topTenCustomers = (topCustomers || []).slice(0, 10);
  const topCustomerChartData = topTenCustomers.map((customer) => ({
    name: customer.name,
    bookings: Number(customer.booking_count || 0),
    amount: Number(customer.total_spent || 0),
  }));

  const handleDateRangeChange = (nextStartDate, nextEndDate) => {
    if (!nextStartDate && !nextEndDate) {
      setStartDate(null);
      setEndDate(null);
    } else if (nextStartDate?.isValid?.() && nextEndDate?.isValid?.()) {
      setStartDate(nextStartDate);
      setEndDate(nextEndDate);
    }
  };

  const handleDownloadPdf = async () => {
    if (!statistics || loading || exportInProgressRef.current || !reportRef.current) return;
    exportInProgressRef.current = true;
    document.body.classList.add('dashboard-pdf-exporting');
    setPdfExporting(true);
    try {
      const rangeLabel = startDate && endDate
        ? `${startDate.format('DD MMM YYYY')} to ${endDate.format('DD MMM YYYY')}`
        : 'All';
      const filenameRange = startDate && endDate
        ? `${startDate.format('YYYY-MM-DD')}-${endDate.format('YYYY-MM-DD')}`
        : 'all';

      await new Promise((resolve) => setTimeout(resolve, 120));
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      if (document.fonts?.ready) {
        await document.fonts.ready;
      }

      const logoDataUrl = await loadRoomnexaLogoDataUrl();
      const canvas = await html2canvas(reportRef.current, {
        backgroundColor: '#ffffff',
        scale: Math.min(2, window.devicePixelRatio || 1),
        useCORS: true,
        allowTaint: true,
        logging: false,
        windowWidth: reportRef.current.scrollWidth,
        windowHeight: reportRef.current.scrollHeight,
        onclone: (documentClone, clonedElement) => {
          documentClone.body.style.backgroundColor = '#ffffff';
          if (clonedElement) {
            clonedElement.style.backgroundColor = '#ffffff';
          }
        },
      });

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const headerHeight = 28;
      const footerHeight = 13;
      const imageTop = headerHeight + margin;
      const printableHeight = pageHeight - imageTop - footerHeight - margin;
      const maxImageWidth = pageWidth - margin * 2;
      let pageIndex = 1;

      const drawPdfHeader = (pageNumber) => {
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pageWidth, headerHeight, 'F');
        if (logoDataUrl) {
          pdf.addImage(logoDataUrl, 'PNG', (pageWidth - 36) / 2, 3.4, 36, 10);
        } else {
          pdf.setTextColor(30, 41, 59);
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(18);
          pdf.text('RoomNexa', pageWidth / 2, 8.5, { align: 'center' });
        }
        pdf.setTextColor(30, 41, 59);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        pdf.text('Customer Dashboard Report', pageWidth / 2, 18.6, { align: 'center' });
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(7);
        pdf.setTextColor(100, 116, 139);
        pdf.text(
          `Range: ${rangeLabel} | Generated: ${moment().format('DD MMM YYYY, hh:mm A')} | Page ${pageNumber}`,
          pageWidth / 2,
          23.6,
          { align: 'center' }
        );
        pdf.setDrawColor(226, 232, 240);
        pdf.line(margin, headerHeight - 2, pageWidth - margin, headerHeight - 2);
      };

      const drawPdfFooter = (pageNumber) => {
        const footerTop = pageHeight - footerHeight;
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, footerTop, pageWidth, footerHeight, 'F');
        pdf.setDrawColor(226, 232, 240);
        pdf.line(margin, footerTop, pageWidth - margin, footerTop);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(7.5);
        pdf.setTextColor(30, 41, 59);
        pdf.text('Powered by RoomNexa', margin, footerTop + 5.2);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(6.8);
        pdf.setTextColor(100, 116, 139);
        pdf.text('Professional guest intelligence for smarter hotel relationships.', margin, footerTop + 9.2);
        pdf.text(`Page ${pageNumber}`, pageWidth - margin, footerTop + 7.4, { align: 'right' });
      };

      const imageData = canvas.toDataURL('image/png');
      const imageWidth = maxImageWidth;
      const imageHeight = (canvas.height * imageWidth) / canvas.width;
      const imageLeft = (pageWidth - imageWidth) / 2;
      let renderedHeight = 0;
      while (renderedHeight < imageHeight) {
        drawPdfHeader(pageIndex);
        pdf.addImage(imageData, 'PNG', imageLeft, imageTop - renderedHeight, imageWidth, imageHeight);
        drawPdfFooter(pageIndex);
        renderedHeight += printableHeight;
        if (renderedHeight < imageHeight) {
          pdf.addPage();
          pageIndex += 1;
        }
      }

      const reportTableRows = monthlyGrowth.map((row) => [
        row.name,
        formatNumber(row.served),
        formatNumber(row.repeated),
        formatNumber(row.direct),
        formatNumber(row.ota),
      ]);

      if (reportTableRows.length) {
        pdf.addPage();
        pageIndex += 1;
        drawPdfHeader(pageIndex);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(12);
        pdf.setTextColor(30, 41, 59);
        pdf.text('Customer Booking Details', margin, imageTop);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(7.5);
        pdf.setTextColor(100, 116, 139);
        pdf.text('Period-wise served customers, repeated guests, and booking source customer split.', margin, imageTop + 5);
        drawPdfFooter(pageIndex);

        autoTable(pdf, {
          startY: imageTop + 10,
          head: [['Date', 'Total Customer', 'Repeated Customer', 'Direct Booking Customer', 'OTA Booking Customer']],
          body: reportTableRows,
          margin: { top: imageTop, right: margin, bottom: footerHeight + 7, left: margin },
          styles: {
            font: 'helvetica',
            fontSize: 7,
            cellPadding: 1.5,
            overflow: 'linebreak',
            valign: 'top',
            lineColor: [226, 232, 240],
            lineWidth: 0.1,
            textColor: [51, 65, 85],
          },
          headStyles: {
            fillColor: [30, 41, 59],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
          },
          alternateRowStyles: { fillColor: [248, 250, 252] },
          columnStyles: {
            0: { cellWidth: 34 },
            1: { cellWidth: 34 },
            2: { cellWidth: 40 },
            3: { cellWidth: 43 },
            4: { cellWidth: 39 },
          },
          didParseCell: (data) => {
            if (data.section === 'body' && data.column.index > 0) {
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.textColor = [15, 23, 42];
            }
          },
          didDrawPage: () => {
            const currentPage = pdf.internal.getCurrentPageInfo().pageNumber;
            drawPdfHeader(currentPage);
            drawPdfFooter(currentPage);
          },
        });
      }

      pdf.save(`customer-center-${filenameRange}.pdf`);
    } finally {
      document.body.classList.remove('dashboard-pdf-exporting');
      setPdfExporting(false);
      exportInProgressRef.current = false;
    }
  };

  if (false && loading) {
    return (
      <Box
        sx={{
          p: 3,
          backgroundColor: isDark ? '#12151c' : '#f2f4f7',
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (false && error) {
    return (
      <Box
        sx={{
          p: 3,
          backgroundColor: isDark ? '#12151c' : '#f2f4f7',
          minHeight: '100vh',
        }}
      >
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }
  const dashboardReady = Boolean(statistics) || loading;
  const metricsLoading = loading && !statistics;
  const growthLoading = loading && monthlyGrowth.length === 0;
  const locationLoading = loading && locationChartData.length === 0;
  const ageLoading = loading && ageChartData.length === 0;
  const topCustomersLoading = loading && topCustomerChartData.length === 0;
  const repeatedAgeLoading = loading && !repeatedGuestAgeDistribution;

  return (
    <Box
      sx={{
        p: 3,
        backgroundColor: isDark ? '#12151c' : '#f2f4f7',
        minHeight: '100vh',
        transition: 'background-color 0.3s ease',
      }}
    >
      <Box sx={{ mb: 3 }}>
        <DashboardHeader
          title="Customer Dashboard"
          showCustomerSectionTabs
          navLinks={[
            { url: '/finance/rates/', label: 'Manage Rate' },
          ]}
        />
      </Box>

      <DashboardReportHeader
        title="Customer center insights"
        subtitle="Guest growth, loyalty, demographics and top-customer analytics in one view."
        startDate={startDate}
        endDate={endDate}
        onDateRangeChange={handleDateRangeChange}
        onRefresh={fetchData}
        onPdfDownload={handleDownloadPdf}
        loading={loading}
        pdfExporting={pdfExporting}
        pdfDisabled={!statistics}
        themeMode={currentTheme}
        accentColor={themeColors.total}
        initialPreset="this_month"
        allowAllPreset
      />

      {error && (
        <Box sx={{ mb: 3 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {!error && dashboardReady && (
        <Box
          ref={reportRef}
          sx={{
            backgroundColor: currentTheme === 'light' ? '#f2f4f7' : 'transparent',
          }}
        >
          <Grid container spacing={getInsightsGridSpacing()} sx={{ mb: getInsightsGridSpacing() }}>
            <Grid item {...getInsightsMetricGridProps()}>
              <DashboardMetricCard
                variant="performance"
                loading={metricsLoading}
                title="Total Guests"
                value={statistics?.total_guests || 0}
                unit="guests"
                data={monthlyGrowth.map((row) => ({ name: row.name, value: row.total || row.served || 0 }))}
                color={themeColors.total}
                icon={<PeopleIcon sx={{ color: 'inherit' }} />}
                theme={currentTheme}
                formatValue={formatNumber}
              />
            </Grid>
            <Grid item {...getInsightsMetricGridProps()}>
              <DashboardMetricCard
                variant="performance"
                loading={metricsLoading}
                title="Active Guests"
                value={statistics?.active_guests || 0}
                unit="active guests"
                data={monthlyGrowth.map((row) => ({ name: row.name, value: row.active || row.total || 0 }))}
                color={themeColors.active}
                icon={<PersonIcon sx={{ color: 'inherit' }} />}
                theme={currentTheme}
                formatValue={formatNumber}
              />
            </Grid>
            <Grid item {...getInsightsMetricGridProps()}>
              <DashboardMetricCard
                variant="performance"
                loading={metricsLoading}
                title="New Guests / Repeated Guests"
                value={`${statistics?.new_guests || 0}/${statistics?.repeated_guests || 0}`}
                unit="guests"
                data={monthlyGrowth.map((row) => ({ name: row.name, value: row.new || 0 }))}
                color={themeColors.new}
                icon={<PersonAddIcon sx={{ color: 'inherit' }} />}
                theme={currentTheme}
              />
            </Grid>
            <Grid item {...getInsightsMetricGridProps()}>
              <DashboardMetricCard
                variant="performance"
                loading={metricsLoading}
                title="Loyalty Members"
                value={statistics?.loyalty_members || 0}
                unit="members"
                data={monthlyGrowth.map((row) => ({ name: row.name, value: row.loyalty || 0 }))}
                color={themeColors.loyalty}
                icon={<LoyaltyIcon sx={{ color: 'inherit' }} />}
                theme={currentTheme}
                formatValue={formatNumber}
              />
            </Grid>
          </Grid>

          <Grid container spacing={getInsightsGridSpacing()} sx={{ mb: getInsightsGridSpacing() }}>
            <Grid item xs={12}>
              <DashboardChartCard
                variant="performance"
                loading={growthLoading}
                eyebrow="GUEST GROWTH ANALYSIS"
                title=""
                subtitle="Total, new, and repeated customers for the selected range"
                data={monthlyGrowth}
                chartType="area"
                dataKeys={[
                  { dataKey: 'total', name: 'Total Guests', color: themeColors.total },
                  { dataKey: 'new', name: 'New Guests', color: themeColors.new },
                  { dataKey: 'repeated', name: 'Repeated Customers', color: themeColors.loyalty },
                ]}
                theme={currentTheme}
                height={340}
                showArea
                formatValue={formatNumber}
              />
            </Grid>
          </Grid>

          <Grid container spacing={getInsightsGridSpacing()} sx={{ mb: getInsightsGridSpacing(), alignItems: 'stretch' }}>
            <Grid item {...getInsightsSplitChartGridProps()} sx={{ display: 'flex' }}>
              <Box sx={{ width: '100%', minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <DashboardChartCard
                variant="performance"
                loading={locationLoading}
                eyebrow="GUEST LOCATION DISTRIBUTION"
                title=""
                subtitle={`Distribution by ${locationGroupBy}`}
                chartType="pie"
                pieData={locationChartData}
                pieAsBars
                pieBarExportLimit={6}
                theme={currentTheme}
                height={320}
                formatValue={formatNumber}
              />
              </Box>
            </Grid>
            <Grid item {...getInsightsSplitChartGridProps()} sx={{ display: 'flex' }}>
              <Box sx={{ width: '100%', minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <DashboardChartCard
                variant="performance"
                loading={ageLoading}
                eyebrow="CUSTOMER BY AGE GROUP"
                title=""
                subtitle="Distribution of guests by age ranges"
                chartType="pie"
                pieData={ageChartData}
                theme={currentTheme}
                height={320}
                formatValue={formatNumber}
              />
              </Box>
            </Grid>
          </Grid>

          <Grid container spacing={getInsightsGridSpacing()} sx={{ alignItems: 'stretch' }}>
            <Grid item {...getInsightsChartPrimaryGridProps()} sx={{ display: 'flex' }}>
              <Box sx={{ width: '100%', minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <DashboardChartCard
                variant="performance"
                loading={topCustomersLoading}
                eyebrow="TOP REPEAT CUSTOMERS"
                title=""
                subtitle="Top 10 customers ranked equally by bookings and total spend"
                data={topCustomerChartData}
                chartType="bar"
                dataKeys={[
                  { dataKey: 'bookings', name: 'Bookings', color: themeColors.loyalty, yAxisKey: 'left' },
                  { dataKey: 'amount', name: 'Total Spend', color: themeColors.total, yAxisKey: 'right' },
                ]}
                theme={currentTheme}
                height={360}
                formatValue={formatNumber}
              />
              </Box>
            </Grid>
            <Grid item {...getInsightsChartSecondaryGridProps()} sx={{ display: 'flex' }}>
              <Box sx={{ width: '100%', minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <DashboardChartCard
                variant="performance"
                loading={repeatedAgeLoading}
                eyebrow="REPEATED GUESTS BY AGE"
                title=""
                subtitle={
                  repeatedGuestTotal > 0
                    ? `${repeatedGuestRate.toFixed(1)}% repeat rate (${formatNumber(repeatedGuestCount)} of ${formatNumber(repeatedGuestTotal)} guests)`
                    : 'Share of repeated guests by age group'
                }
                chartType="pie"
                pieData={repeatedAgeChartData}
                pieAsBars
                theme={currentTheme}
                height={360}
                formatValue={formatNumber}
              />
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );

  return (
    <Box 
      sx={{ 
        p: 3,
        backgroundColor: isDark ? '#12151c' : '#f2f4f7',
        minHeight: '100vh',
        transition: 'background-color 0.3s ease',
      }}
    >
      <Box sx={{ mb: 3 }}>
        <DashboardHeader
          title="Customer Dashboard"
          showCustomerSectionTabs
          navLinks={[
            { url: '/finance/rates/', label: 'Manage Rate' },
          ]}
        />
      </Box>

      {/* Metric Gauges */}
      <Grid container spacing={getInsightsGridSpacing()} sx={{ mb: getInsightsGridSpacing() }}>
        <Grid item {...getInsightsMetricGridProps()}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              backgroundColor: isDark ? '#2a2a31' : '#ffffff',
              border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
              borderRadius: 2,
              transition: 'background-color 0.3s ease, border-color 0.3s ease',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, width: '100%', justifyContent: 'center' }}>
              <PeopleIcon sx={{ color: themeColors.total, fontSize: 20 }} />
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: isDark ? '#ddd' : '#212121',
                  textTransform: 'uppercase',
                  fontSize: '0.75rem',
                  transition: 'color 0.3s ease',
                }}
              >
                Total Guests
              </Typography>
            </Box>
            <Box sx={{ width: '100%', height: 220, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <Gauge
                value={statistics?.total_guests || 0}
                valueMax={totalGuestsMax}
                startAngle={-110}
                endAngle={110}
                sx={{
                  [`& .${gaugeClasses.valueText}`]: {
                    fontSize: 36,
                    fontWeight: 700,
                    fill: themeColors.total,
                    transform: 'translate(0px, 0px)',
                  },
                  [`& .${gaugeClasses.valueArc}`]: {
                    fill: themeColors.total,
                    strokeWidth: 12,
                  },
                  [`& .${gaugeClasses.referenceArc}`]: {
                    fill: isDark ? '#333246' : '#e0e0e0',
                    strokeWidth: 12,
                  },
                }}
                text={({ value }) => value.toLocaleString()}
              />
              <Typography
                variant="caption"
                sx={{
                  color: isDark ? '#999' : '#757575',
                  fontSize: '0.7rem',
                  mt: 1,
                  textAlign: 'center',
                }}
              >
                {totalGuestsMax > 0 ? ((statistics?.total_guests || 0) / totalGuestsMax * 100).toFixed(1) : 0}% of total
              </Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item {...getInsightsMetricGridProps()}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              backgroundColor: isDark ? '#2a2a31' : '#ffffff',
              border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
              borderRadius: 2,
              transition: 'background-color 0.3s ease, border-color 0.3s ease',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, width: '100%', justifyContent: 'center' }}>
              <PersonIcon sx={{ color: themeColors.active, fontSize: 20 }} />
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: isDark ? '#ddd' : '#212121',
                  textTransform: 'uppercase',
                  fontSize: '0.75rem',
                  transition: 'color 0.3s ease',
                }}
              >
                Active Guests
              </Typography>
            </Box>
            <Box sx={{ width: '100%', height: 220, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <Gauge
                value={statistics?.active_guests || 0}
                valueMax={totalGuestsMax}
                startAngle={-110}
                endAngle={110}
                sx={{
                  [`& .${gaugeClasses.valueText}`]: {
                    fontSize: 36,
                    fontWeight: 700,
                    fill: themeColors.active,
                    transform: 'translate(0px, 0px)',
                  },
                  [`& .${gaugeClasses.valueArc}`]: {
                    fill: themeColors.active,
                    strokeWidth: 12,
                  },
                  [`& .${gaugeClasses.referenceArc}`]: {
                    fill: isDark ? '#333246' : '#e0e0e0',
                    strokeWidth: 12,
                  },
                }}
                text={({ value }) => value.toLocaleString()}
              />
              <Typography
                variant="caption"
                sx={{
                  color: isDark ? '#999' : '#757575',
                  fontSize: '0.7rem',
                  mt: 1,
                  textAlign: 'center',
                }}
              >
                {totalGuestsMax > 0 ? ((statistics?.active_guests || 0) / totalGuestsMax * 100).toFixed(1) : 0}% of total
              </Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item {...getInsightsMetricGridProps()}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              backgroundColor: isDark ? '#2a2a31' : '#ffffff',
              border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
              borderRadius: 2,
              transition: 'background-color 0.3s ease, border-color 0.3s ease',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, width: '100%', justifyContent: 'center' }}>
              <PersonAddIcon sx={{ color: themeColors.new, fontSize: 20 }} />
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: isDark ? '#ddd' : '#212121',
                  textTransform: 'uppercase',
                  fontSize: '0.75rem',
                  transition: 'color 0.3s ease',
                }}
              >
                New Guests
              </Typography>
            </Box>
            <Box sx={{ width: '100%', height: 220, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <Gauge
                value={statistics?.new_guests || 0}
                valueMax={totalGuestsMax}
                startAngle={-110}
                endAngle={110}
                sx={{
                  [`& .${gaugeClasses.valueText}`]: {
                    fontSize: 36,
                    fontWeight: 700,
                    fill: themeColors.new,
                    transform: 'translate(0px, 0px)',
                  },
                  [`& .${gaugeClasses.valueArc}`]: {
                    fill: themeColors.new,
                    strokeWidth: 12,
                  },
                  [`& .${gaugeClasses.referenceArc}`]: {
                    fill: isDark ? '#333246' : '#e0e0e0',
                    strokeWidth: 12,
                  },
                }}
                text={({ value }) => value.toLocaleString()}
              />
              <Typography
                variant="caption"
                sx={{
                  color: isDark ? '#999' : '#757575',
                  fontSize: '0.7rem',
                  mt: 1,
                  textAlign: 'center',
                }}
              >
                {totalGuestsMax > 0 ? ((statistics?.new_guests || 0) / totalGuestsMax * 100).toFixed(1) : 0}% of total
              </Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item {...getInsightsMetricGridProps()}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              backgroundColor: isDark ? '#2a2a31' : '#ffffff',
              border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
              borderRadius: 2,
              transition: 'background-color 0.3s ease, border-color 0.3s ease',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, width: '100%', justifyContent: 'center' }}>
              <LoyaltyIcon sx={{ color: themeColors.loyalty, fontSize: 20 }} />
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: isDark ? '#ddd' : '#212121',
                  textTransform: 'uppercase',
                  fontSize: '0.75rem',
                  transition: 'color 0.3s ease',
                }}
              >
                Loyalty Members
              </Typography>
            </Box>
            <Box sx={{ width: '100%', height: 220, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <Gauge
                value={statistics?.loyalty_members || 0}
                valueMax={totalGuestsMax}
                startAngle={-110}
                endAngle={110}
                sx={{
                  [`& .${gaugeClasses.valueText}`]: {
                    fontSize: 36,
                    fontWeight: 700,
                    fill: themeColors.loyalty,
                    transform: 'translate(0px, 0px)',
                  },
                  [`& .${gaugeClasses.valueArc}`]: {
                    fill: themeColors.loyalty,
                    strokeWidth: 12,
                  },
                  [`& .${gaugeClasses.referenceArc}`]: {
                    fill: isDark ? '#333246' : '#e0e0e0',
                    strokeWidth: 12,
                  },
                }}
                text={({ value }) => value.toLocaleString()}
              />
              <Typography
                variant="caption"
                sx={{
                  color: isDark ? '#999' : '#757575',
                  fontSize: '0.7rem',
                  mt: 1,
                  textAlign: 'center',
                }}
              >
                {totalGuestsMax > 0 ? ((statistics?.loyalty_members || 0) / totalGuestsMax * 100).toFixed(1) : 0}% of total
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Monthly Growth Chart - Full Width */}
      <Grid container spacing={getInsightsGridSpacing()} sx={{ mb: getInsightsGridSpacing() }}>
        <Grid item xs={12}>
          <DashboardChartCard
            title="Guest Growth Analysis"
            subtitle="Compare and analyze data across categories by layering data series - Total Guests and New Guests over the last 12 months"
            data={monthlyGrowth}
            chartType="area"
            dataKeys={[
              { dataKey: 'total', name: 'Total Guests', color: themeColors.total },
              { dataKey: 'new', name: 'New Guests', color: themeColors.new },
            ]}
            theme={currentTheme}
            height={350}
            showArea={true}
          />
        </Grid>
      </Grid>

      {/* Location Distribution and Age Group - Side by Side */}
      <Grid container spacing={getInsightsGridSpacing()} sx={{ mb: getInsightsGridSpacing() }}>
        <Grid item xs={12} sm={6}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              backgroundColor: isDark ? '#2a2a31' : '#ffffff',
              border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
              borderRadius: 2,
              transition: 'background-color 0.3s ease, border-color 0.3s ease',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: isDark ? '#ddd' : '#212121',
                    mb: 0.5,
                    transition: 'color 0.3s ease',
                  }}
                >
                  Guest Location Distribution
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: isDark ? '#999' : '#757575',
                    fontSize: '0.875rem',
                    transition: 'color 0.3s ease',
                  }}
                >
                  Percentage distribution by {locationGroupBy === 'country' ? 'Country' : locationGroupBy === 'state' ? 'State' : 'City'}
                </Typography>
              </Box>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel sx={{ color: isDark ? '#ddd' : '#212121' }}>Group By</InputLabel>
                <Select
                  value={locationGroupBy}
                  label="Group By"
                  onChange={(e) => setLocationGroupBy(e.target.value)}
                  sx={{
                    color: isDark ? '#ddd' : '#212121',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: isDark ? '#333246' : '#e0e0e0',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: isDark ? '#444' : '#bdbdbd',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: themeColors.total,
                    },
                  }}
                >
                  <MenuItem value="country">Country</MenuItem>
                  <MenuItem value="state">State</MenuItem>
                  <MenuItem value="city">City</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box
              sx={{
                height: 400,
                overflowY: 'auto',
                pr: 1,
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: isDark ? '#333246' : '#f5f5f5',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: isDark ? '#555' : '#bdbdbd',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: isDark ? '#666' : '#9e9e9e',
                  },
                },
              }}
            >
              {locationDistribution[locationGroupBy] && locationDistribution[locationGroupBy].length > 0 ? (
                (() => {
                  const total = locationDistribution[locationGroupBy].reduce((sum, item) => sum + item.value, 0);
                  const colors = [
                    themeColors.total,
                    themeColors.active,
                    themeColors.new,
                    themeColors.loyalty,
                    '#ff9800',
                    '#9c27b0',
                    '#00bcd4',
                    '#8bc34a',
                    '#e91e63',
                    '#3f51b5',
                  ];
                  return locationDistribution[locationGroupBy].map((item, index) => {
                    const percentage = total > 0 ? (item.value / total) * 100 : 0;
                    return (
                      <Box key={index} sx={{ mb: 2.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: isDark ? '#ddd' : '#212121',
                              fontWeight: 500,
                              fontSize: '0.875rem',
                            }}
                          >
                            {item.name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                color: isDark ? '#999' : '#757575',
                                fontSize: '0.75rem',
                              }}
                            >
                              {item.value} guests
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: colors[index % colors.length],
                                fontWeight: 600,
                                fontSize: '0.875rem',
                                minWidth: '45px',
                                textAlign: 'right',
                              }}
                            >
                              {percentage.toFixed(1)}%
                            </Typography>
                          </Box>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={percentage}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: isDark ? '#333246' : '#e0e0e0',
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 4,
                              backgroundColor: colors[index % colors.length],
                            },
                          }}
                        />
                      </Box>
                    );
                  });
                })()
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: isDark ? '#999' : '#757575',
                    }}
                  >
                    No location data available
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              backgroundColor: isDark ? '#2a2a31' : '#ffffff',
              border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
              borderRadius: 2,
              transition: 'background-color 0.3s ease, border-color 0.3s ease',
              height: '100%',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: isDark ? '#ddd' : '#212121',
                mb: 0.5,
                transition: 'color 0.3s ease',
              }}
            >
              Customer by Age Group
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: isDark ? '#999' : '#757575',
                fontSize: '0.875rem',
                mb: 2,
                transition: 'color 0.3s ease',
              }}
            >
              Distribution of guests by age ranges
            </Typography>
            <Box sx={{ width: '100%', minHeight: 300 }}>
              {ageGroupDistribution && ageGroupDistribution.length > 0 ? (
                <DashboardChartCard
                  title=""
                  subtitle=""
                  data={[]}
                  chartType="pie"
                  pieData={ageGroupDistribution.map((item, index) => {
                    // Generate colors for pie chart
                    const colors = [
                      themeColors.total,
                      themeColors.active,
                      themeColors.new,
                      themeColors.loyalty,
                      '#ff9800',
                      '#9c27b0',
                      '#00bcd4',
                      '#8bc34a',
                    ];
                    return {
                      name: `${item.name} years`,
                      value: item.value,
                      color: colors[index % colors.length],
                    };
                  })}
                  theme={currentTheme}
                  height={300}
                  showToggle={false}
                />
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: isDark ? '#999' : '#757575',
                    }}
                  >
                    No age group data available
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Top 5 Customers Table */}
      <Grid container spacing={getInsightsGridSpacing()}>
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              backgroundColor: isDark ? '#2a2a31' : '#ffffff',
              border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
              borderRadius: 2,
              transition: 'background-color 0.3s ease, border-color 0.3s ease',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: isDark ? '#ddd' : '#212121',
                mb: 2,
                transition: 'color 0.3s ease',
              }}
            >
              Top 5 Repeat Customers
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: isDark ? '#999' : '#757575',
                mb: 3,
                fontSize: '0.875rem',
                transition: 'color 0.3s ease',
              }}
            >
              Customers with the most bookings and highest spending
            </Typography>

            {topCustomers.length === 0 ? (
              <Typography
                variant="body2"
                sx={{
                  color: isDark ? '#999' : '#757575',
                  textAlign: 'center',
                  py: 4,
                }}
              >
                No repeat customers found
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: isDark ? '#ddd' : '#212121', fontWeight: 600 }}>
                        Rank
                      </TableCell>
                      <TableCell sx={{ color: isDark ? '#ddd' : '#212121', fontWeight: 600 }}>
                        Customer Name
                      </TableCell>
                      <TableCell sx={{ color: isDark ? '#ddd' : '#212121', fontWeight: 600 }}>
                        Phone
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ color: isDark ? '#ddd' : '#212121', fontWeight: 600 }}
                      >
                        Total Bookings
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: isDark ? '#ddd' : '#212121', fontWeight: 600 }}
                      >
                        Total Amount
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topCustomers.map((customer, index) => (
                      <TableRow key={customer.id} hover>
                        <TableCell sx={{ color: isDark ? '#ddd' : '#212121' }}>
                          #{index + 1}
                        </TableCell>
                        <TableCell sx={{ color: isDark ? '#ddd' : '#212121', fontWeight: 500 }}>
                          {customer.name}
                        </TableCell>
                        <TableCell sx={{ color: isDark ? '#999' : '#757575' }}>
                          {customer.phone}
                        </TableCell>
                        <TableCell align="center" sx={{ color: isDark ? '#ddd' : '#212121' }}>
                          {customer.booking_count}
                        </TableCell>
                        <TableCell align="right" sx={{ color: themeColors.total, fontWeight: 600 }}>
                          {formatCurrency(customer.total_spent, customer.currency)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CustomerHubPage;
